import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MessageSquare, Plus, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { FeedbackDialog, TYPE_LABEL, STATUS_LABEL, useAboutResolver } from "@/components/feedback/FeedbackDialog";
import { useCatalogue } from "@/context/CatalogueContext";
import { api, errorMessage } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

export const TypeBadge = ({ type }) => (
  <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium", type === "guardrail" && "border-destructive bg-destructive/10 text-destructive")}>
    {type === "guardrail" && <ShieldAlert className="!h-3.5 !w-3.5" aria-hidden="true" />}{TYPE_LABEL[type]}
  </span>
);

export default function Feedback() {
  const [params, setParams] = useSearchParams();
  const resolve = useAboutResolver();
  const { loading } = useCatalogue();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const aboutParam = params.get("about");

  const load = useCallback(() => api.get("/feedback/mine").then(({ data }) => { setItems(data); setError(""); }).catch((e) => setError(errorMessage(e))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (aboutParam && !loading) setOpen(true); }, [aboutParam, loading]);

  const close = (o) => { setOpen(o); if (!o && aboutParam) setParams({}, { replace: true }); };

  return (
    <div data-testid="feedback-page">
      <PageHeader title="My feedback" description="What you have sent, its status and the Owner's reply."
        actions={<Button onClick={() => setOpen(true)} data-testid="feedback-new-button"><Plus className="!h-4 !w-4" /> Give feedback</Button>} />
      {error ? <ErrorState message={error} onRetry={load} /> : !items ? <Skeleton className="h-40 rounded-xl" /> : items.length === 0 ? (
        <EmptyState icon={MessageSquare} title="You have not sent any feedback yet" body="Use Give feedback on any agent, workflow or team page, or the button above." testid="feedback-empty" />
      ) : (
        <ul className="max-w-4xl space-y-3" data-testid="my-feedback-list">
          {items.map((f) => (
            <li key={f.id} className={cn("card-elevated p-5", f.type === "guardrail" && "border-l-4 border-l-destructive")} data-testid={`my-feedback-${f.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <TypeBadge type={f.type} />
                <span className="font-medium">{f.about_name}</span>
                <span className="text-muted-foreground">· rating {f.rating} of 5 · {formatDateTime(f.created_at)}</span>
                <span className="ml-auto rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium" data-testid="my-feedback-status">{STATUS_LABEL[f.status]}</span>
              </div>
              <p className="mt-3 whitespace-pre-wrap text-sm">{f.text}</p>
              {f.reply && <p className="mt-3 rounded-lg bg-muted px-3 py-2 text-sm" data-testid="my-feedback-reply"><span className="font-medium">Reply from {f.replied_by || "the Owner"}: </span>{f.reply}</p>}
            </li>
          ))}
        </ul>
      )}
      <FeedbackDialog open={open} onOpenChange={close} defaultAbout={resolve(aboutParam)} onSent={load} />
    </div>
  );
}
